import React, { useState, useEffect } from 'react';
import {
  Compass,
  Target,
  Wrench,
  Calendar,
  Sparkles,
  PieChart,
  Clock,
  Activity,
  Gem,
  Heart,
  Hash,
  Home,
  FileText,
  Layers,
  ChevronLeft,
  ChevronRight,
  X,
  ScrollText,
  FileDown,
  Timer,
  Globe,
  Sliders,
  Briefcase,
  User,
  LogOut
} from 'lucide-react';
import { Language } from '../types/kundali';

export type WorkspaceMode = 'enterprise-studio' | 'astrologer' | 'tools' | 'synthesis' | 'dossier';

export interface CollapsibleSidebarProps {
  activeWorkspaceMode: WorkspaceMode;
  onSelectWorkspaceMode: (mode: WorkspaceMode) => void;
  activeFeatureTab?: string;
  onSelectFeatureTab?: (tab: string) => void;
  lang?: Language | string;
  setLang?: (lang: Language) => void;
  isOpenOnMobile: boolean;
  onCloseMobile: () => void;
  onOpenPanchang?: () => void;
  onOpenShubha?: () => void;
  onOpenBlueprint?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onNewChart?: () => void;
  onOpenVault?: () => void;
  onPrint?: () => void;
  onOpenBTR?: () => void;
  onOpenSettings?: () => void;
  onOpenAdminPortal?: () => void;
  onOpenAuth?: () => void;
  onLogout?: () => void;
  currentUser?: any;
}

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  activeWorkspaceMode,
  onSelectWorkspaceMode,
  activeFeatureTab,
  onSelectFeatureTab,
  lang = 'en',
  setLang,
  isOpenOnMobile,
  onCloseMobile,
  onOpenPanchang,
  onOpenShubha,
  onOpenBlueprint,
  isCollapsed: controlledIsCollapsed,
  onToggleCollapse: controlledOnToggleCollapse,
  onNewChart,
  onOpenVault,
  onPrint,
  onOpenBTR,
  onOpenSettings,
  onOpenAdminPortal,
  onOpenAuth,
  onLogout,
  currentUser
}) => {
  const isHi = lang === 'hi' || lang === 'sa' || lang === 'mr';

  const getUserInitial = (): string => {
    if (currentUser?.displayName) return currentUser.displayName.trim()[0]?.toUpperCase() || 'V';
    if (currentUser?.name) return currentUser.name.trim()[0]?.toUpperCase() || 'V';
    if (currentUser?.email) return currentUser.email.trim()[0]?.toUpperCase() || 'V';
    return 'B';
  };

  const getUserDisplayName = (): string => {
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.name) return currentUser.name;
    if (currentUser?.email) return currentUser.email.split('@')[0];
    return isHi ? 'अतिथि जातक' : 'Guest Astrologer';
  };

  const getUserTier = (): string => {
    if (currentUser?.tier) return String(currentUser.tier).replace('_', ' ');
    if (currentUser?.role) return String(currentUser.role).replace('_', ' ');
    return 'PRO ASTRO';
  };

  const isManagementUser = Boolean(
    currentUser && (
      currentUser.role === 'admin' ||
      currentUser.role === 'super_admin' ||
      currentUser.activeRole === 'admin' ||
      currentUser.activeRole === 'super_admin' ||
      currentUser.role === 'ADMIN' ||
      currentUser.role === 'SUPER_ADMIN' ||
      currentUser.activeRole === 'ADMIN' ||
      currentUser.activeRole === 'SUPER_ADMIN' ||
      currentUser.role === 'consulting_astrologer' ||
      currentUser.tier === 'ACHARYA'
    )
  );

  // Desktop collapse state (persisted in localStorage or controlled via props)
  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('astro_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const isCollapsed = controlledIsCollapsed !== undefined ? controlledIsCollapsed : internalCollapsed;

  const toggleCollapse = () => {
    if (controlledOnToggleCollapse) {
      controlledOnToggleCollapse();
    } else {
      setInternalCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem('astro_sidebar_collapsed', String(next));
        } catch {}
        return next;
      });
    }
  };

  // Keyboard shortcut: Ctrl+B to toggle desktop collapse, Escape to close mobile drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        toggleCollapse();
      }
      if (e.key === 'Escape' && isOpenOnMobile) {
        onCloseMobile();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpenOnMobile, onCloseMobile]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (isOpenOnMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpenOnMobile]);

  const handleWorkspaceClick = (mode: WorkspaceMode) => {
    onSelectWorkspaceMode(mode);
    if (isOpenOnMobile) {
      onCloseMobile();
    }
  };

  const handleFeatureClick = (tabKey: string, customHandler?: () => void) => {
    if (customHandler) {
      customHandler();
    } else if (onSelectFeatureTab) {
      onSelectFeatureTab(tabKey);
    } else {
      window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: tabKey }));
    }
    if (isOpenOnMobile) {
      onCloseMobile();
    }
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpenOnMobile && (
        <div
          id="sidebar-mobile-backdrop"
          onClick={onCloseMobile}
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 lg:hidden transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      {/* Main Sidebar Shell */}
      <aside
        id="collapsible-sidebar"
        role="navigation"
        aria-label={isHi ? 'वैदिक नेविगेशन' : 'Vedic Workspace Navigation'}
        className={`fixed inset-y-0 left-0 z-50 lg:static flex flex-col bg-[#061A19] transition-all duration-300 ease-in-out shrink-0 select-none shadow-2xl lg:shadow-none ${
          isCollapsed ? 'lg:w-0 lg:overflow-hidden lg:border-r-0' : 'lg:w-64 border-r border-white/10'
        } ${isOpenOnMobile ? 'w-64 translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Top Header inside Sidebar */}
        <div className="h-13 border-b border-white/10 px-3 flex items-center justify-between shrink-0">
          <div className={`flex items-center gap-2 overflow-hidden ${isCollapsed ? 'lg:hidden' : ''}`}>
            <span className="text-xs font-bold uppercase tracking-wider text-[#D4AF37]">
              {isHi ? 'नेविगेशन' : 'Navigation'}
            </span>
          </div>

          {/* Desktop Collapse Toggle Button */}
          <button
            type="button"
            id="sidebar-collapse-toggle"
            onClick={toggleCollapse}
            aria-label={isCollapsed ? (isHi ? 'मेनू फैलाएं' : 'Expand Sidebar (Ctrl+B)') : (isHi ? 'मेनू सिकोड़ें' : 'Collapse Sidebar (Ctrl+B)')}
            title={isCollapsed ? (isHi ? 'मेनू फैलाएं (Ctrl+B)' : 'Expand Sidebar (Ctrl+B)') : (isHi ? 'मेनू सिकोड़ें (Ctrl+B)' : 'Collapse Sidebar (Ctrl+B)')}
            className="hidden lg:flex w-9 h-9 items-center justify-center rounded-xl text-[#A6BCB4] hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {/* Mobile Close Button */}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label={isHi ? 'मेनू बंद करें' : 'Close Sidebar'}
            className="flex lg:hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-[#A6BCB4] hover:text-white hover:bg-white/10 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Quick Actions Strip (Relocated from Top Header Dock) */}
        <div className={`p-2.5 pb-2 shrink-0 border-b border-white/10 ${isCollapsed ? 'lg:hidden' : 'block'}`}>
          <div className="grid grid-cols-2 gap-1.5">
            {/* 1. New Chart */}
            <button
              type="button"
              id="sidebar-btn-new-chart"
              onClick={() => {
                if (onNewChart) onNewChart();
                if (isOpenOnMobile) onCloseMobile();
              }}
              title={isHi ? 'नई जन्म कुंडली बनाएं' : 'New Birth Chart'}
              className="min-h-[44px] flex items-center gap-2 px-2.5 py-2 rounded-xl bg-emerald-950/30 hover:bg-emerald-900/50 border border-emerald-500/30 hover:border-emerald-400 text-emerald-300 text-xs font-semibold transition cursor-pointer shadow-2xs group"
            >
              <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0 group-hover:scale-110 transition-transform" />
              <span className="truncate">{isHi ? 'नई कुंडली' : 'New Chart'}</span>
            </button>

            {/* 2. Kundali Vault */}
            <button
              type="button"
              id="sidebar-btn-vault"
              onClick={() => {
                if (onOpenVault) onOpenVault();
                if (isOpenOnMobile) onCloseMobile();
              }}
              title={isHi ? 'सहेजी गई कुंडलियां' : 'Kundali Vault'}
              className="min-h-[44px] flex items-center gap-2 px-2.5 py-2 rounded-xl bg-amber-950/30 hover:bg-amber-900/50 border border-amber-500/30 hover:border-amber-400 text-amber-300 text-xs font-semibold transition cursor-pointer shadow-2xs group"
            >
              <ScrollText className="w-3.5 h-3.5 text-amber-400 shrink-0 group-hover:scale-110 transition-transform" />
              <span className="truncate">{isHi ? 'वॉल्ट' : 'Vault'}</span>
            </button>

            {/* 3. Print Report */}
            <button
              type="button"
              id="sidebar-btn-print"
              onClick={() => {
                if (onPrint) onPrint();
                if (isOpenOnMobile) onCloseMobile();
              }}
              title={isHi ? 'प्रिंट रिपोर्ट / पीडीएफ' : 'Print / Export PDF'}
              className="min-h-[44px] flex items-center gap-2 px-2.5 py-2 rounded-xl bg-indigo-950/30 hover:bg-indigo-900/50 border border-indigo-500/30 hover:border-indigo-400 text-indigo-300 text-xs font-semibold transition cursor-pointer shadow-2xs group"
            >
              <FileDown className="w-3.5 h-3.5 text-indigo-400 shrink-0 group-hover:scale-110 transition-transform" />
              <span className="truncate">{isHi ? 'प्रिंट' : 'Print'}</span>
            </button>

            {/* 4. Birth Time Rectification (BTR) */}
            <button
              type="button"
              id="sidebar-btn-btr"
              onClick={() => {
                if (onOpenBTR) onOpenBTR();
                if (isOpenOnMobile) onCloseMobile();
              }}
              title={isHi ? 'जन्म समय शोधन (BTR)' : 'Birth Time Rectification (BTR)'}
              className="min-h-[44px] flex items-center gap-2 px-2.5 py-2 rounded-xl bg-rose-950/30 hover:bg-rose-900/50 border border-rose-500/30 hover:border-rose-400 text-rose-300 text-xs font-semibold transition cursor-pointer shadow-2xs group"
            >
              <Timer className="w-3.5 h-3.5 text-rose-400 shrink-0 group-hover:scale-110 transition-transform" />
              <span className="truncate font-bold">BTR</span>
            </button>
          </div>
        </div>

        {/* Scrollable Navigation Body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 space-y-4 custom-scrollbar">

          {/* ============================================================ */}
          {/* SECTION 1: CORE PRIMARY WORKSPACES                           */}
          {/* ============================================================ */}
          <div>
            {!isCollapsed && (
              <div className="px-2.5 mb-1 text-[11px] font-bold text-[#A6BCB4] uppercase tracking-wider">
                {isHi ? 'मुख्य कार्यक्षेत्र' : 'Core Workspaces'}
              </div>
            )}
            <div className="space-y-1">
              {/* 1. Vedic Destiny Studio */}
              <button
                type="button"
                id="sidebar-nav-destiny"
                onClick={() => handleWorkspaceClick('enterprise-studio')}
                aria-current={activeWorkspaceMode === 'enterprise-studio' ? 'page' : undefined}
                title={isHi ? 'वैदिक डेस्टिनी स्टूडियो' : 'Vedic Destiny Studio'}
                aria-label={isHi ? 'वैदिक डेस्टिनी स्टूडियो' : 'Vedic Destiny Studio'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group ${
                  activeWorkspaceMode === 'enterprise-studio'
                    ? 'bg-[#D4AF37] text-[#082120] font-bold shadow-xs border border-[#D4AF37]'
                    : 'text-[#E0EAE6] hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <Compass className={`w-4 h-4 shrink-0 ${activeWorkspaceMode === 'enterprise-studio' ? 'text-[#082120]' : 'text-[#D4AF37]'}`} />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'वैदिक डेस्टिनी' : 'Vedic Destiny'}
                </span>
              </button>

              {/* 2. Astrologer's Workbench */}
              <button
                type="button"
                id="sidebar-nav-workbench"
                onClick={() => handleWorkspaceClick('astrologer')}
                aria-current={activeWorkspaceMode === 'astrologer' ? 'page' : undefined}
                title={isHi ? 'कुंडली कार्यपीठ' : "Astrologer's Workbench"}
                aria-label={isHi ? 'कुंडली कार्यपीठ' : "Astrologer's Workbench"}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group ${
                  activeWorkspaceMode === 'astrologer'
                    ? 'bg-[#D4AF37] text-[#082120] font-bold shadow-xs border border-[#D4AF37]'
                    : 'text-[#E0EAE6] hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <Target className={`w-4 h-4 shrink-0 ${activeWorkspaceMode === 'astrologer' ? 'text-[#082120]' : 'text-[#A6BCB4] group-hover:text-[#D4AF37]'}`} />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'कार्यपीठ' : 'Workbench'}
                </span>
              </button>

              {/* 3. Tools & Calculations */}
              <button
                type="button"
                id="sidebar-nav-tools"
                onClick={() => handleWorkspaceClick('tools')}
                aria-current={activeWorkspaceMode === 'tools' ? 'page' : undefined}
                title={isHi ? 'ज्योतिषी उपकरण व चार्ट' : 'Tools & Calculations'}
                aria-label={isHi ? 'ज्योतिषी उपकरण व चार्ट' : 'Tools & Calculations'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group ${
                  activeWorkspaceMode === 'tools'
                    ? 'bg-[#D4AF37] text-[#082120] font-bold shadow-xs border border-[#D4AF37]'
                    : 'text-[#E0EAE6] hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <Wrench className={`w-4 h-4 shrink-0 ${activeWorkspaceMode === 'tools' ? 'text-[#082120]' : 'text-[#A6BCB4] group-hover:text-[#D4AF37]'}`} />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'उपकरण व चार्ट' : 'Tools & Charts'}
                </span>
              </button>

              {/* 4. Targeted Synthesis & Timing */}
              <button
                type="button"
                id="sidebar-nav-synthesis"
                onClick={() => handleWorkspaceClick('synthesis')}
                aria-current={activeWorkspaceMode === 'synthesis' ? 'page' : undefined}
                title={isHi ? 'संश्लेषण एवं समय निर्धारण' : 'Synthesis & Timing'}
                aria-label={isHi ? 'संश्लेषण एवं समय निर्धारण' : 'Synthesis & Timing'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group ${
                  activeWorkspaceMode === 'synthesis'
                    ? 'bg-[#D4AF37] text-[#082120] font-bold shadow-xs border border-[#D4AF37]'
                    : 'text-[#E0EAE6] hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <Layers className={`w-4 h-4 shrink-0 ${activeWorkspaceMode === 'synthesis' ? 'text-[#082120]' : 'text-[#A6BCB4] group-hover:text-[#D4AF37]'}`} />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'संश्लेषण' : 'Synthesis & Timing'}
                </span>
              </button>

              {/* 5. Astrologer's Diagnostic Dossier */}
              <button
                type="button"
                id="sidebar-nav-dossier"
                onClick={() => handleWorkspaceClick('dossier')}
                aria-current={activeWorkspaceMode === 'dossier' ? 'page' : undefined}
                title={isHi ? 'ज्योतिषी निदान डॉसियर' : 'Diagnostic Dossier'}
                aria-label={isHi ? 'ज्योतिषी निदान डॉसियर' : 'Diagnostic Dossier'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group ${
                  activeWorkspaceMode === 'dossier'
                    ? 'bg-[#D4AF37] text-[#082120] font-bold shadow-xs border border-[#D4AF37]'
                    : 'text-[#E0EAE6] hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <FileText className={`w-4 h-4 shrink-0 ${activeWorkspaceMode === 'dossier' ? 'text-[#082120]' : 'text-[#A6BCB4] group-hover:text-[#D4AF37]'}`} />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'निदान डॉसियर' : 'Diagnostic Dossier'}
                </span>
              </button>
            </div>
          </div>

          {/* ============================================================ */}
          {/* SECTION 2: DAILY ESSENTIALS (RELOCATED FROM TOP HEADER)       */}
          {/* ============================================================ */}
          <div>
            {!isCollapsed && (
              <div className="px-2.5 mb-1 text-[11px] font-bold text-[#A6BCB4] uppercase tracking-wider">
                {isHi ? 'दैनिक तत्व' : 'Daily Essentials'}
              </div>
            )}
            <div className="space-y-1">
              {/* Daily Panchang */}
              <button
                type="button"
                id="sidebar-nav-daily-panchang"
                onClick={() => handleFeatureClick('panchang', onOpenPanchang)}
                aria-current={activeFeatureTab === 'panchang' || activeFeatureTab === 'daily-panchang' ? 'page' : undefined}
                title={isHi ? 'दैनिक पंचांग एवं मुहूर्त' : 'Daily Panchang & Muhurta'}
                aria-label={isHi ? 'दैनिक पंचांग एवं मुहूर्त' : 'Daily Panchang & Muhurta'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group border ${
                  activeFeatureTab === 'panchang' || activeFeatureTab === 'daily-panchang'
                    ? 'bg-[#174C4B] border-[#D4AF37]/50 text-white shadow-xs'
                    : 'border-amber-400/20 bg-amber-950/20 text-amber-300 hover:bg-amber-950/40 hover:border-amber-400/40'
                }`}
              >
                <Calendar className="w-4 h-4 shrink-0 text-amber-400" />
                <div className={`truncate flex flex-col ${isCollapsed ? 'lg:hidden' : 'flex'}`}>
                  <span className="font-bold">{isHi ? 'दैनिक पंचांग' : 'Daily Panchang'}</span>
                  <span className="text-[10px] text-amber-300/70">{isHi ? 'दैनिक काल स्थिति' : 'Tithi & Nakshatra'}</span>
                </div>
              </button>

              {/* Shubha Essentials */}
              <button
                type="button"
                id="sidebar-nav-shubha-essentials"
                onClick={() => handleFeatureClick('shubha-essentials', onOpenShubha)}
                aria-current={activeFeatureTab === 'shubha-essentials' ? 'page' : undefined}
                title={isHi ? 'शुभ तत्व व उपाय (रंग, अंक, मुहूर्त)' : 'Shubha Essentials (Colors, Numbers, Remedies)'}
                aria-label={isHi ? 'शुभ तत्व व उपाय' : 'Shubha Essentials & Auspicious Timing'}
                className={`w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-left cursor-pointer group border ${
                  activeFeatureTab === 'shubha-essentials'
                    ? 'bg-[#174C4B] border-[#D4AF37]/50 text-white shadow-xs'
                    : 'border-rose-400/20 bg-rose-950/20 text-rose-300 hover:bg-rose-950/40 hover:border-rose-400/40'
                }`}
              >
                <Sparkles className="w-4 h-4 shrink-0 text-rose-400" />
                <div className={`truncate flex flex-col ${isCollapsed ? 'lg:hidden' : 'flex'}`}>
                  <span className="font-bold">{isHi ? 'शुभ तत्व व उपाय' : 'Shubha Essentials'}</span>
                  <span className="text-[10px] text-rose-300/70">{isHi ? 'शुभ रंग, अंक व दिशा' : 'Colors, Numbers & Upayas'}</span>
                </div>
              </button>
            </div>
          </div>

          {/* ============================================================ */}
          {/* SECTION 3: ASTROLOGICAL FEATURES & MODULES                  */}
          {/* ============================================================ */}
          <div>
            {!isCollapsed && (
              <div className="px-2.5 mb-1 text-[11px] font-bold text-[#A6BCB4] uppercase tracking-wider">
                {isHi ? 'ज्योतिषीय घटक' : 'Astrological Modules'}
              </div>
            )}
            <div className="space-y-0.5">
              {/* Charts & Vargas */}
              <button
                type="button"
                onClick={() => handleFeatureClick('charts')}
                aria-current={activeFeatureTab === 'charts' ? 'page' : undefined}
                title={isHi ? 'कुंडली व षोडशवर्ग (D1–D60)' : 'D1 & Shodashvarga Charts (D1–D60)'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <PieChart className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'षोडशवर्ग चार्ट' : 'Charts & Vargas (D1–D60)'}
                </span>
              </button>

              {/* Dasha Timelines */}
              <button
                type="button"
                onClick={() => handleFeatureClick('dashas')}
                aria-current={activeFeatureTab === 'dashas' ? 'page' : undefined}
                title={isHi ? 'विंशोत्तरी दशा समय चक्र' : 'Vimshottari Dasha Timeline'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Clock className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'दशा प्रणाली' : 'Dasha Systems (MD/AD)'}
                </span>
              </button>

              {/* Gochar & Live Transits */}
              <button
                type="button"
                onClick={() => handleFeatureClick('transits')}
                aria-current={activeFeatureTab === 'transits' ? 'page' : undefined}
                title={isHi ? 'गोचर व तात्कालिक ग्रह संचरण' : 'Gochar & Planetary Transits'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Activity className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'गोचर संचरण' : 'Gochar Transits'}
                </span>
              </button>

              {/* Gemstone Advisor */}
              <button
                type="button"
                onClick={() => handleFeatureClick('gemstones')}
                aria-current={activeFeatureTab === 'gemstones' ? 'page' : undefined}
                title={isHi ? 'रत्न परामर्श एवं अनुकूलता' : 'Classical Gemstone Advisor'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Gem className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'रत्न परामर्श' : 'Gemstone Advisor'}
                </span>
              </button>

              {/* Remedies Bank */}
              <button
                type="button"
                onClick={() => handleFeatureClick('remedies')}
                aria-current={activeFeatureTab === 'remedies' ? 'page' : undefined}
                title={isHi ? 'शास्त्रीय उपाय एवं निवारण बैंक' : 'Classical Remedies & Upaya Bank'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <span className="w-4 h-4 shrink-0 text-center font-serif text-sm font-bold text-[#D4AF37]">ॐ</span>
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'उपाय बैंक' : 'Remedies Bank'}
                </span>
              </button>

              {/* Kundali Matching */}
              <button
                type="button"
                onClick={() => handleFeatureClick('matching')}
                aria-current={activeFeatureTab === 'matching' ? 'page' : undefined}
                title={isHi ? 'अष्टकूट मिलान व गुण विचार' : 'Kundali Milan & Compatibility'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Heart className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'कुंडली मिलान' : 'Kundali Matching'}
                </span>
              </button>

              {/* Numerology */}
              <button
                type="button"
                onClick={() => handleFeatureClick('numerology')}
                aria-current={activeFeatureTab === 'numerology' ? 'page' : undefined}
                title={isHi ? 'वैदिक अंकशास्त्र (मूलांक व भाग्यांक)' : 'Vedic Numerology (Moolank & Bhagyank)'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Hash className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'अंकशास्त्र' : 'Vedic Numerology'}
                </span>
              </button>

              {/* Vastu Shastra */}
              <button
                type="button"
                onClick={() => handleFeatureClick('vastu')}
                aria-current={activeFeatureTab === 'vastu' ? 'page' : undefined}
                title={isHi ? 'वास्तु शास्त्र दिशात्मक विश्लेषण' : 'Vastu Shastra Directional Analysis'}
                className="w-full min-h-[40px] flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-[#E0EAE6] hover:bg-white/5 hover:text-white transition-all text-left cursor-pointer group"
              >
                <Home className="w-4 h-4 shrink-0 text-[#D4AF37]" />
                <span className={`truncate ${isCollapsed ? 'lg:hidden' : 'block'}`}>
                  {isHi ? 'वास्तु शास्त्र' : 'Vastu Shastra'}
                </span>
              </button>
            </div>
          </div>

        </div>

        {/* Sidebar Footer with System & Profile Dock */}
        <div className={`p-2.5 border-t border-white/10 shrink-0 space-y-2 bg-[#041211] ${isCollapsed ? 'lg:hidden' : 'block'}`}>
          {/* Row 1: Language Switcher, Admin Portal, Settings */}
          <div className="flex items-center justify-between gap-1.5">
            {setLang && (
              <button
                type="button"
                id="sidebar-btn-lang"
                onClick={() => setLang(lang === 'hi' ? 'en' : 'hi')}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-[#A6BCB4] hover:text-white transition cursor-pointer border border-white/5 hover:border-white/20 shadow-2xs"
                title={isHi ? 'भाषा बदलें (अंग्रेजी / हिन्दी)' : 'Switch Language (EN / HI)'}
              >
                <Globe className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="font-bold uppercase text-[10px]">{lang === 'hi' ? 'HI' : 'EN'}</span>
              </button>
            )}

            {isManagementUser && onOpenAdminPortal && (
              <button
                type="button"
                id="sidebar-btn-management"
                onClick={() => {
                  onOpenAdminPortal();
                  if (isOpenOnMobile) onCloseMobile();
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-950/40 hover:bg-amber-900/50 text-xs text-amber-300 border border-amber-500/30 hover:border-amber-400 transition cursor-pointer shadow-2xs"
                title="Management Portal"
              >
                <Briefcase className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[10px] font-bold">{isHi ? 'प्रबंधन' : 'Admin'}</span>
              </button>
            )}

            {onOpenSettings && (
              <button
                type="button"
                id="sidebar-btn-settings"
                onClick={() => {
                  onOpenSettings();
                  if (isOpenOnMobile) onCloseMobile();
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-[#A6BCB4] hover:text-white transition cursor-pointer border border-white/5 hover:border-white/20 shadow-2xs"
                title={isHi ? 'सेटिंग्स एवं वर्शन इतिहास' : 'Settings & Version History'}
              >
                <Sliders className="w-3.5 h-3.5 text-[#A6BCB4]" />
                <span className="text-[11px] font-medium">{isHi ? 'सेटिंग्स' : 'Settings'}</span>
              </button>
            )}
          </div>

          {/* Row 2: User Profile Account & Sign Out */}
          <div className="flex items-center justify-between pt-1 border-t border-white/5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full bg-[#174C4B] border border-[#24827D] text-[#FAF6ED] flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs">
                {getUserInitial()}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold text-[#FAF6ED] truncate max-w-[120px]">{getUserDisplayName()}</span>
                <span className="text-[8.5px] text-[#A6BCB4] font-mono uppercase">{getUserTier()}</span>
              </div>
            </div>

            {currentUser ? (
              <button
                type="button"
                id="sidebar-btn-logout"
                onClick={() => {
                  if (onLogout) onLogout();
                  if (isOpenOnMobile) onCloseMobile();
                }}
                className="p-1.5 rounded-lg text-[#A6BCB4] hover:text-rose-400 hover:bg-white/5 transition cursor-pointer"
                title={isHi ? 'लॉग आउट' : 'Sign Out'}
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                id="sidebar-btn-auth"
                onClick={() => {
                  if (onOpenAuth) onOpenAuth();
                  if (isOpenOnMobile) onCloseMobile();
                }}
                className="text-[11px] font-bold text-[#D4AF37] hover:underline cursor-pointer"
              >
                {isHi ? 'लॉगिन' : 'Sign In'}
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};
